import axios from "axios";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, RotateCw } from "lucide-react";
import { API, th, td, StatPill, Empty, money } from "./shared";

function FailedRow({ r, onDone }) {
  const [busy, setBusy] = useState(false);
  const retry = async () => {
    setBusy(true);
    try {
      const url = r.kind === "payout" ? `${API}/admin/zoho/payouts/${r.id}/retry` : `${API}/admin/invoices/${r.id}/zoho-sync`;
      const { data } = await axios.post(url);
      const ok = r.kind === "payout" ? data.zoho_payment_status === "synced" : data.zoho_status === "synced";
      ok ? toast.success(`${r.invoice_number} synced to Zoho`) : toast.error(data.zoho_payment_error || data.zoho_error || "Still failing");
      onDone();
    } catch (e) { toast.error(e.response?.data?.detail || "Retry failed"); } finally { setBusy(false); }
  };
  return (
    <tr data-testid={`zsr-failed-row-${r.kind}-${r.id}`} style={{ borderBottom: "1px solid #e5e7eb" }}>
      <td style={td}><b>{r.invoice_number}</b><div style={{ fontSize: 10.5, color: "#9ca3af" }}>GPP-{r.order_id}</div></td>
      <td style={td}>{r.kind === "payout" ? "Supplier payout" : r.type_label}</td>
      <td style={td}>{r.party || "—"}</td>
      <td style={{ ...td, textAlign: "right", fontWeight: 600 }}>{money(r.amount_inr)}</td>
      <td style={{ ...td, color: "#B91C1C", fontSize: 11.5, maxWidth: 280 }} data-testid={`zsr-failed-error-${r.id}`}>{r.error || "—"}</td>
      <td style={td}>
        <button data-testid={`zsr-retry-${r.kind}-${r.id}`} className="secondary-btn" style={{ fontSize: 11.5, padding: "5px 10px" }} disabled={busy} onClick={retry}>
          <RotateCw size={12}/> {busy ? "Retrying…" : "Retry"}
        </button>
      </td>
    </tr>
  );
}

function DailyTable({ daily }) {
  const cell = (n, bad) => <span style={{ fontWeight: n ? 700 : 400, color: n ? (bad ? "#B91C1C" : "#15803D") : "#9ca3af" }}>{n}</span>;
  return (
    <table data-testid="zsr-daily-table" style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
      <thead><tr style={{ background: "#f3f4f6", textAlign: "left" }}>
        <th style={th}>Date</th><th style={th}>Invoices synced</th><th style={th}>Invoices failed</th><th style={th}>Payouts recorded</th><th style={th}>Payouts failed</th>
      </tr></thead>
      <tbody>{daily.map(d => (
        <tr key={d.date} data-testid={`zsr-day-${d.date}`} style={{ borderBottom: "1px solid #e5e7eb" }}>
          <td style={td}>{new Date(d.date).toLocaleDateString("en-IN", { day: "2-digit", month: "short", weekday: "short" })}</td>
          <td style={td}>{cell(d.invoices_synced)}</td><td style={td}>{cell(d.invoices_failed, true)}</td>
          <td style={td}>{cell(d.payouts_synced)}</td><td style={td}>{cell(d.payouts_failed, true)}</td>
        </tr>))}
      </tbody>
    </table>
  );
}

export function ZohoSyncReport() {
  const [days, setDays] = useState(14);
  const [data, setData] = useState(null);
  const load = () => axios.get(`${API}/admin/zoho/sync-report`, { params: { days } }).then(r => setData(r.data)).catch(() => setData({ daily: [], failed: [], pending_payouts: [], totals: {}, today: {} }));
  useEffect(() => { load(); }, [days]); // eslint-disable-line react-hooks/exhaustive-deps
  const t = data?.today || {};
  return (
    <section className="content" data-testid="zoho-sync-report-page">
      <div className="section-heading">
        <div>
          <p className="eyebrow">Bookkeeping</p>
          <h2>Zoho sync report</h2>
          <p style={{ margin: "4px 0 0", fontSize: 13, color: "#64748b" }}>Daily summary of invoices pushed to Zoho Books and Razorpay supplier payouts recorded as vendor payments against the supplier bill.</p>
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          <select data-testid="zsr-days-select" value={days} onChange={e => setDays(Number(e.target.value))} style={{ border: "1px solid #CBD5E1", borderRadius: 7, padding: "6px 8px", fontSize: 12 }}>
            {[7, 14, 30, 90].map(n => <option key={n} value={n}>Last {n} days</option>)}
          </select>
          <button data-testid="zsr-refresh" className="secondary-btn" onClick={load}><RefreshCw size={14}/> Refresh</button>
        </div>
      </div>
      {!data ? <div style={{ fontSize: 13, color: "#64748b" }}>Loading…</div> : (
        <>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", margin: "0 0 14px" }}>
            <StatPill label="Today · synced" value={t.invoices_synced || 0} tone="ok" testid="zsr-today-synced"/>
            <StatPill label="Today · failed" value={t.invoices_failed || 0} tone={t.invoices_failed ? "warn" : undefined} testid="zsr-today-failed"/>
            <StatPill label="Today · payouts" value={t.payouts_synced || 0} tone="ok" testid="zsr-today-payouts"/>
            <StatPill label="Needs retry" value={data.failed.length} tone={data.failed.length ? "warn" : undefined} testid="zsr-failed-count"/>
            <StatPill label="Zoho" value={data.zoho_enabled ? "Connected" : "Sync off"} testid="zsr-zoho-status"/>
          </div>
          <h3 style={{ fontSize: 14, margin: "18px 0 8px" }}>Failed — needs retry</h3>
          {data.failed.length === 0 ? <Empty text="Nothing failed. All invoices and payouts are in Zoho."/> : (
            <div style={{ overflowX: "auto" }} data-testid="zsr-failed-table">
              <table style={{ width: "100%", fontSize: 12.5, borderCollapse: "collapse" }}>
                <thead><tr style={{ background: "#f3f4f6", textAlign: "left" }}>
                  <th style={th}>Document</th><th style={th}>Type</th><th style={th}>Party</th><th style={{ ...th, textAlign: "right" }}>Amount</th><th style={th}>Error</th><th style={th}></th>
                </tr></thead>
                <tbody>{data.failed.map(r => <FailedRow key={`${r.kind}-${r.id}`} r={r} onDone={load}/>)}</tbody>
              </table>
            </div>
          )}
          {data.pending_payouts.length > 0 && (
            <p data-testid="zsr-pending-payouts" style={{ fontSize: 12, color: "#B45309", margin: "10px 0 0" }}>
              {data.pending_payouts.length} payout(s) waiting to be recorded in Zoho{data.zoho_enabled ? "" : " — Zoho sync is switched off"}.
            </p>
          )}
          <h3 style={{ fontSize: 14, margin: "22px 0 8px" }}>Daily summary</h3>
          <div style={{ overflowX: "auto" }}><DailyTable daily={data.daily}/></div>
        </>
      )}
    </section>
  );
}
